import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import jobIssueFactory, { IJobIssue } from 'Scripts/models/jobIssueModel';
import { popModal } from 'Scripts/actions';
import componentServices from 'Scripts/componentServices';
import { getModel, ModelCollection } from 'Scripts/store';
import { IJobCostWorkArea } from 'Scripts/models/jobCostWorkAreaModel';

interface KeyValue {
    key: string;
    value: string;

}

interface IssueSource extends KeyValue {
    issues: KeyValue[]
}

export default function JobIssueEditModal({issue, inspectionConfig, saveIssue}: {issue: IJobIssue, inspectionConfig: { issueSources: IssueSource[], issueResovlers: KeyValue[], resolutionEstimateMinutes: string[] }, saveIssue: (issue: IJobIssue) => void}) {
	const { store } = componentServices;
	const jobCost = getModel({ store, collection: ModelCollection.jobCosts, key: issue.jobCostKey});
	const workAreas = useMemo(() => {
		return sortBy(jobCost.workAreas, 'sortIndex') as IJobCostWorkArea[];
	}, [jobCost]);
	const { issueSources, issueResovlers, resolutionEstimateMinutes } = inspectionConfig;
	const [mutableIssue, setMutableIssue] = useState(() => jobIssueFactory(issue.toDocument()));
	const updateModel = useCallback((path: keyof IJobIssue, value: string) => {
		const updated = jobIssueFactory(mutableIssue.toDocument());

		updated.setAt(path, value);

		setMutableIssue(updated);
        
	}, [mutableIssue]);
	const updateSource = useCallback((value: string) => {
		const updated = jobIssueFactory(mutableIssue.toDocument());

		updated.source = value;
		updated.issue = '';
		updated.deficiencyCode = '';

		setMutableIssue(updated);
        
	}, [mutableIssue]);
	const updateIssue = useCallback((value: string) => {
		const updated = jobIssueFactory(mutableIssue.toDocument());

		updated.issue = value;

		if(!value) {
			updated.deficiencyCode = '';
		}
		else {
			const source = issueSources.find(s => s.value === updated.source);

			if(source) {
				const issue = source.issues.find(i => i.value === value);

				if(issue) {
					updated.deficiencyCode = `${source.key}-${issue.key}`;
				}
			}
		}

		setMutableIssue(updated);
        
	}, [mutableIssue]);
	const isValid = mutableIssue.isValid();
	const selectedSource = issueSources.find(s => s.value === mutableIssue.source);

	return <div className='window'>
		<header><button className='cancel' onClick={popModal}><span>Cancel</span></button><span>Edit &bull; 1 Issue</span><button className={isValid? 'save': 'save disabled'} onClick={ isValid? () => saveIssue(mutableIssue): undefined}><span>Save</span></button></header>
		<div className='views'>
			<div className='content in scrollable'>
				<table className='editIssue' cellPadding='0' cellSpacing='0'>
					<tbody>
						{ 
							issue.workAreaKey? null: <tr>
								<td></td>
								<td><strong>Work Area</strong></td>
								<td>
									<select className="select" placeholder="Select" value={mutableIssue.workAreaKey} onChange={e => updateModel('workAreaKey', e.target.value)}>
										<option value="">Select</option>
										{ workAreas.map(wa => <option key={wa.key} value={wa.key}>{wa.name}</option>)}
									</select>
								</td>
								<td></td>
							</tr>
						}
						<tr>
							<td></td>
							<td><strong>Source</strong></td>
							<td>
								<select className="select" placeholder="Select" value={mutableIssue.source} onChange={e => updateSource(e.target.value)}>
									<option value="">Select</option>
									{ issueSources.map(s => <option key={s.value} value={s.value}>{s.value}</option>) }
								</select>
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Issue</strong></td>
							<td>
								<select className="select" placeholder="Select" value={mutableIssue.issue} onChange={e => updateIssue(e.target.value)}>
									<option value="">Select</option>
									{ selectedSource? selectedSource.issues.map(i => <option key={i.value} value={i.value}>{i.value}</option>): null }
								</select>
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Description</strong></td>
							<td><textarea placeholder='Description' onChange={e => updateModel('description', e.target.value)} value={mutableIssue.description} /></td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Material</strong></td>
							<td>
								<table className='compact' cellPadding='0' cellSpacing='0'>
									<tbody>
										<tr className=''>
											<td></td>
											<td className=''>
												<select className="select" placeholder="Manufacturer">
													<option value="">Select Manufacturer</option>
													<option value="">Kitchen Craft</option>
													<option value="">Warehouse</option>
													<option value="">Another</option>
												</select>
											</td>
											<td className=''><input type='text' placeholder='Product Name' autoCapitalize='' autoCorrect='off' value='' />
											</td>
											<td className=''><input type='text' placeholder='PO Number' value='' /></td>
											<td className='action'><button className='icon iconOnly addItem'><span></span></button></td>
											<td></td>
										</tr>
										<tr className=''>
											<td></td>
											<td className=''>
												<select className="select" placeholder="Manufacturer">
													<option value="">Select Manufacturer</option>
													<option value="" selected>Kitchen Craft</option>
													<option value="">Warehouse</option>
													<option value="">Another</option>
												</select>
											</td>
											<td className=''><input type='text' placeholder='Product Name' autoCapitalize='' autoCorrect='off' value='1-WEP11' />
											</td>
											<td className=''><input type='text' placeholder='PO Number' value='56784' /></td>
											<td className='action'><button className='icon iconOnly contextMenu'><span></span></button></td>
											<td></td>
										</tr>
									</tbody>
								</table>
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Estimate</strong></td>
							<td>
								<select className="select" placeholder="Estimate (optional)" value={mutableIssue.viewValue('estimatedMinutes')} onChange={e => updateModel('estimatedMinutes', e.target.value)}>
									<option value="">Estimate (optional)</option>
									{ resolutionEstimateMinutes.map(e => <option key={e} value={e}>{e}</option>)}
								</select>
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Personnel</strong></td>
							<td>
								<select className="select" placeholder="Who should resolve this? (optional)" value={mutableIssue.resolver} onChange={e => updateModel('resolver', e.target.value)}>
									<option value="">Who should resolve this? (optional)</option>
									{ issueResovlers.map(r => <option key={r.key} value={r.key}>{r.value}</option>)}
								</select>
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Photos</strong></td>
							<td>
								<div className="photos">
									<div className="photoControl large">
										<div className="uploadPhoto add"></div>
										<input type="file" accept="image/*" capture="environment" />
									</div>
									<div className="photoControl large">
										<div className="uploadPhoto" style={{ backgroundImage: 'url("/images/_demo_photo2.jpg")'}}></div>
									</div>
								</div>
							</td>
							<td></td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
}